import React, { useEffect, useRef } from 'react';
import { MapContainer, TileLayer, GeoJSON, Marker, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import ReactLeafletGoogleLayer from 'react-leaflet-google-layer';
import L from 'leaflet';
import { useWindowSize } from 'react-use';
import { MarkLocations } from "../features/task-manager";
import { useStore } from "../helpers";
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

// Fix for Vite
delete (L.Icon.Default.prototype as any)._getIconUrl;

L.Icon.Default.mergeOptions({
    iconRetinaUrl: markerIcon2x,
    iconUrl: markerIcon,
    shadowUrl: markerShadow
});

// SAFER SetBounds (prevents crash)
const SetBounds = ({ bounds }) => {
    const map = useMap();
    if (!bounds || !bounds.isValid() || !bounds.isValid()) return null;

    try {
        map.fitBounds(bounds);
    } catch (e) {
        console.warn("fitBounds failed", e);
    }
    return null;
};

// Map Controller Component - watches for center/zoom changes and updates map
// Based on React Leaflet best practices: https://react-leaflet.js.org/docs/api-components/#using-hooks
const MapController = ({ center, zoom }) => {
    const map = useMap();
    const hasInitialized = useRef(false);

    useEffect(() => {
        if (center && Array.isArray(center) && center.length === 2 &&
            typeof center[0] === 'number' && typeof center[1] === 'number' && map) {

            const targetZoom = zoom || 15;

            console.log("MapController: center/zoom provided", { center, targetZoom, hasInitialized: hasInitialized.current });

            // Always update on first render with center, or on subsequent changes
            if (!hasInitialized.current) {
                hasInitialized.current = true;
                console.log("MapController: First initialization with center");
            }

            // Use setView without animation for immediate positioning
            try {
                console.log("MapController: Calling setView with", center, targetZoom);
                map.setView(center, targetZoom, { animate: false });
                console.log("MapController: setView called successfully");

                // Also invalidate size to ensure proper rendering
                setTimeout(() => {
                    map.invalidateSize();
                }, 100);
            } catch (error) {
                console.error("MapController: setView error", error);
            }
        }
    }, [center, zoom, map]);

    return null;
};

const LeafletMapComponent = (props: any) => {
    const rootStore = useStore();
    const { taskManagerStore } = rootStore;
    const { enable_marking_locations } = taskManagerStore;

    const { geojson, mode, task, searchCenter, searchZoom, map_height } = props;
    const { width } = useWindowSize();
    const isMobile = width <= 766;

    // Debug logging
    console.log("LeafletMapComponent render:", { searchCenter, searchZoom, map_height });

    // Compute bounds safely
    let _bounds = null;
    try {
        const layer = L.geoJSON(geojson);
        const b = layer.getBounds();
        if (b.isValid()) _bounds = b;
    } catch (err) {
        console.warn("Invalid GeoJSON bounds");
    }

    let centerFromSurveys = null;
    if (task?.status?.surveys?.length) {
        const first = task.status.surveys.find(s => s.lat && s.long);
        if (first) {
            centerFromSurveys = [first.lat, first.long];
        }
    }

    // Use searchCenter if provided, otherwise use computed center
    const finalCenter = searchCenter && Array.isArray(searchCenter) && searchCenter.length === 2
        ? searchCenter
        : centerFromSurveys
            ? centerFromSurveys
            : _bounds && _bounds.isValid()
                ? [_bounds.getCenter().lat, _bounds.getCenter().lng]
                : [0, 0];

    // Use searchZoom if provided, otherwise default
    const finalZoom = searchZoom || 12;

    const custom_icon = new L.DivIcon({
        className: 'booth-icon',
        html: `<i class="fa-solid fa-map-pin text-3xl" style="color: #B03A2E"></i>`
    });

    // Compute map key for forcing re-render - use timestamp to ensure uniqueness
    const mapKey = searchCenter
        ? `map-search-${searchCenter[0]}-${searchCenter[1]}-${finalZoom}-${Date.now()}`
        : 'map-default';

    console.log("LeafletMapComponent: Using mapKey:", mapKey, "finalCenter:", finalCenter, "finalZoom:", finalZoom);

    // Search location marker icon
    const searchLocationIcon = new L.DivIcon({
        className: 'search-location-icon',
        html: `<i class="fa-solid fa-location-dot text-4xl" style="color: #2563EB"></i>`,
        iconSize: [30, 40],
        iconAnchor: [15, 40]
    });

    return (
        <MapContainer
            key={mapKey}
            style={{
                height: map_height || (isMobile ? 300 : 420),
                width: "100%",
                zIndex: 9999
            }}
            center={finalCenter}
            zoom={finalZoom}
            scrollWheelZoom={true}
            bounds={searchCenter ? undefined : (_bounds || undefined)}
            whenReady={(map) => {
                console.log("MapContainer whenReady - map instance:", map);
                if (searchCenter && Array.isArray(searchCenter) && searchCenter.length === 2) {
                    console.log("MapContainer whenReady - setting view to searchCenter:", searchCenter, finalZoom);
                    // Force the map to the search center after it's ready
                    setTimeout(() => {
                        map.target.setView(searchCenter, finalZoom, { animate: false });
                    }, 100);
                }
            }}
        >
            {mode === "leaflet" ? (
                <TileLayer
                    attribution='© OpenStreetMap contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
            ) : (
                <ReactLeafletGoogleLayer
                    apiKey="AIzaSyCuZfb3JFcDYrKECKU91FKqjK764nvsFGI"
                    type="roadmap"
                />
            )}

            {task?.status?.surveys?.map((item, index) => {
                const { lat, long } = item;
                if (!lat || !long) return null;
                return <Marker key={index} icon={custom_icon} position={[lat, long]} />;
            })}

            {/* Search location marker */}
            {searchCenter && Array.isArray(searchCenter) && searchCenter.length === 2 && (
                <Marker
                    key={`search-marker-${searchCenter[0]}-${searchCenter[1]}`}
                    icon={searchLocationIcon}
                    position={searchCenter as [number, number]}
                />
            )}

            <GeoJSON data={geojson} style={{ fillColor: "#00EDB3", color: "#2E4053" }} />

            {/* Only fit bounds if NO search center is provided */}
            {_bounds && !searchCenter && <SetBounds bounds={_bounds} />}

            {/* MapController always rendered to watch for search center changes */}
            <MapController
                center={searchCenter}
                zoom={searchZoom}
            />

            {enable_marking_locations && <MarkLocations />}
        </MapContainer>
    );
};

export const LeafletMap = LeafletMapComponent;

